import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllComboMeals,
  getSingleComboMeal,
  createComboMeal,
  updateComboMeal,
  deleteComboMeal,
} from "@/api/new-api/staff-management/menu-manage/combo-meal";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks

export const fetchAllComboMeals = createAsyncThunk(
  "comboMeals/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllComboMeals(restaurant_id);
    if (res.status === "success") return res.data.combos;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleComboMeal = createAsyncThunk(
  "comboMeals/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleComboMeal(id);
    if (res.status === "success") return res.data.combo;
    return rejectWithValue(res.message);
  }
);

export const createNewComboMeal = createAsyncThunk(
  "comboMeals/create",
  async (data, { rejectWithValue }) => {
    const res = await createComboMeal(data);
    if (res.status === "success") return res.data.combo;
    return rejectWithValue(res.message);
  }
);

export const updateExistingComboMeal = createAsyncThunk(
  "comboMeals/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateComboMeal(id, data);
    if (res.status === "success") return res.data.combo;
    return rejectWithValue(res.message);
  }
);

export const deleteComboMealById = createAsyncThunk(
  "comboMeals/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteComboMeal(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateComboMealToggle = createAsyncThunk("comboMealToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial State
const initialState = {
  comboMeals: [],
  singleComboMeal: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const comboMealSlice = createSlice({
  name: "comboMeals",
  initialState,
  reducers: {
    clearComboMealMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllComboMeals.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllComboMeals.fulfilled, (state, action) => {
        state.getLoading = false;
        state.comboMeals = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllComboMeals.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleComboMeal.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleComboMeal.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleComboMeal = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleComboMeal.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewComboMeal.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewComboMeal.fulfilled, (state, action) => {
        state.createLoading = false;
        state.comboMeals.push(action.payload);
        state.success = "Combo meal created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewComboMeal.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingComboMeal.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingComboMeal.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.comboMeals = state.comboMeals.map((combo) =>
          combo.id === action.payload.id ? action.payload : combo
        );
        state.success = "Combo meal updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingComboMeal.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteComboMealById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteComboMealById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.comboMeals = state.comboMeals.filter(
          (combo) => combo.id !== action.payload
        );
        state.success = "Combo meal deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteComboMealById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateComboMealToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateComboMealToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.comboMeals = state.comboMeals.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateComboMealToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearComboMealMessages } = comboMealSlice.actions;
export default comboMealSlice.reducer;
